import { computed, ComputedRef, onMounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useTasksStore } from "@/stores/v2";
import { useRoute, useRouter } from "vue-router";
import { TasksService } from "@/services/v2/tasks";
import { normalizeRouteParam } from "@/utils/v2/routes";
import { useTask } from "@/composables/tasks/useTask";
import { ChallengeMaterial } from "@/types/v2/task/challenge/material.types"
import { ChallengeConfiguration } from "@/types/v2/task/challenge/configuration.types"
import { ChallengeDilution } from "@/types/v2/task/challenge/dilution.types"
import { availableTags } from "@/stores/v2/tasks/tags";

import {
  VaccineMethod,
  Tank,
  TankGroup,
} from "@/types/v2";
import { isChallenge } from "@/stores/v2/tasks/utils";

export function useChallenge() {
  // ===================================================================
  // Store & Router
  // ===================================================================
  const router = useRouter();
  const route = useRoute();
  const tasksStore = useTasksStore();
  const { activeTask } = storeToRefs(tasksStore);
  const { startObserver } = useTask();

  // ===================================================================
  // Loading State
  // ===================================================================
  const isLoading = ref(false);
  const error = ref('');

  // ===================================================================
  // State
  // ===================================================================
  const configurations: ComputedRef<ChallengeConfiguration[]> = computed(() => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return [];
    return activeTask.value.details.configurations || [];
  });

  const challengeMaterials: ComputedRef<ChallengeMaterial[]> = computed(() => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return [];
    return activeTask.value.details.challengeMaterials || [];
  });

  const dilutions: ComputedRef<ChallengeDilution[]> = computed(() => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return [];
    return activeTask.value.details.dilutions || [];
  });

  const vaccineMethods: ComputedRef<VaccineMethod[]> = computed(() => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return [];
    return activeTask.value.details.vaccineMethods || [];
  });

  const tanks: ComputedRef<Tank[]> = computed(() => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return [];
    return activeTask.value.details.tanks || [];
  });

  const preChallengeTask = computed(() => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return null;
    return activeTask.value.details.preChallengeTask || null;
  });

  const availableTags = computed(() => {
    if (!activeTask.value) return [];
    return (activeTask.value as any).availableTags || [];
  });

  const equipmentTypes = computed(() => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return [];
    return activeTask.value.details.equipmentTypes || [];
  });

  const equipmentConfigurations = computed(() => {
    if (!activeTask.value || !isChallenge(activeTask.value)) return [];
    return activeTask.value.details.equipmentConfigurations || [];
  });

  // ===================================================================
  // Task Loading Management
  // ===================================================================
  const ensureTaskLoaded = async () => {
    // If task is already loaded and is a challenge task, don't reload
    if (activeTask.value && isChallenge(activeTask.value)) {
      return true;
    }

    // If already loading, don't start another load
    if (isLoading.value) {
      return false;
    }

    isLoading.value = true;
    error.value = '';
    const taskKey = normalizeRouteParam(route.params.key);

    if (!taskKey) {
      error.value = 'Invalid task key';
      isLoading.value = false;
      return false;
    }

    try {
      // Only reset and load if we don't have the right task
      const currentTaskKey = activeTask.value?.key;
      if (currentTaskKey !== taskKey) {
        tasksStore.resetActiveTaskState();
        const task = await TasksService.startTask(taskKey);

        if (!task) {
          error.value = 'Task not found';
          isLoading.value = false;
          return false;
        }

        await tasksStore.setActiveTask(task);
        startObserver();
      }
      
      isLoading.value = false;
      return true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load challenge task';
      isLoading.value = false;
      return false;
    }
  };

  const initFromRoute = async () => {
    await ensureTaskLoaded();
  };

  // ===================================================================
  // Getters
  // ===================================================================
  // Get Groups by Tank key
  const getGroupsOfTank = (key: string): TankGroup[] | [] => {
    if (!tanks.value || !tanks.value.length) return [];

    const tank: Tank | null = tanks.value.find((tank: Tank) => tank.key === key) ?? null;
    if (!tank || !tank.groups) return [];

    return tank.groups;
  }

  // ===================================================================
  // Lifecycle
  // ===================================================================
  // Only watch for route param changes, not activeTask changes
  watch(() => route.params.key, (newKey, oldKey) => {
    if (newKey && newKey !== oldKey) {
      initFromRoute();
    }
  });

  onMounted(() => initFromRoute());

  return {
    // ===================================================================
    // Getters
    // ===================================================================
    getGroupsOfTank,

    // ===================================================================
    // State
    // ===================================================================
    challengeMaterials,
    dilutions,
    vaccineMethods,
    tanks,
    configurations,
    preChallengeTask,
    availableTags,
    equipmentTypes,
    equipmentConfigurations,
    isLoading,
    error,

    // ===================================================================
    // Actions
    // ===================================================================
    ensureTaskLoaded,
  };
}